import {
  RULE_LEVEL_LABELS,
  describeRule,
  roleHas,
  type RuleDto,
  type RuleRunDto,
} from "@iagenix/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { History, MoreHorizontal, Play, Plus, Shield, Trash2, Zap } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { PageHeader } from "@/components/page-header";
import { RunDialog, RunSummary } from "@/components/rules/run-dialog";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { errorMessage } from "@/lib/api";
import { api7, queries7, rulesKey } from "@/lib/fase7-api";
import { queries } from "@/lib/queries";
import { formatDate, formatRelative } from "@/lib/utils";

interface RegrasSearch {
  aba?: "regras" | "historico";
  execucao?: string;
}

export const Route = createFileRoute("/app/$workspaceId/regras")({
  validateSearch: (raw: Record<string, unknown>): RegrasSearch => {
    const out: RegrasSearch = {};
    if (raw.aba === "historico" || raw.aba === "regras") out.aba = raw.aba;
    if (typeof raw.execucao === "string" && raw.execucao) out.execucao = raw.execucao;
    return out;
  },
  component: RegrasPage,
});

function RegrasPage() {
  const { workspaceId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const workspace = useQuery(queries.workspace(workspaceId));
  const canWrite = workspace.data ? roleHas(workspace.data.role, "rules:write") : false;
  const [aba, setAba] = useState<"regras" | "historico">(search.aba ?? "regras");
  const [openRun, setOpenRun] = useState<RuleRunDto | null>(null);
  const fromUrl = useQuery(queries7.ruleRun(workspaceId, search.execucao ?? ""));
  const shownRun = openRun ?? (search.execucao ? (fromUrl.data ?? null) : null);

  return (
    <>
      <PageHeader
        title="Regras automáticas"
        description="SE as métricas baterem nas condições, ENTÃO pausar, ativar, mudar o orçamento ou avisar. Rodam a cada 15, 30 ou 60 minutos, com limites de segurança, e você pode simular antes de executar."
        actions={
          canWrite ? (
            <Button asChild>
              <Link to="/app/$workspaceId/regras/$ruleId" params={{ workspaceId, ruleId: "nova" }}>
                <Plus />
                Nova regra
              </Link>
            </Button>
          ) : null
        }
      />
      <Tabs value={aba} onValueChange={(v) => setAba(v === "historico" ? "historico" : "regras")}>
        <TabsList>
          <TabsTrigger value="regras">
            <Shield className="size-4" /> Regras
          </TabsTrigger>
          <TabsTrigger value="historico">
            <History className="size-4" /> Histórico
          </TabsTrigger>
        </TabsList>
        <TabsContent value="regras">
          <RulesList workspaceId={workspaceId} canWrite={canWrite} onRun={setOpenRun} />
        </TabsContent>
        <TabsContent value="historico">
          <RunsList workspaceId={workspaceId} onOpen={setOpenRun} />
        </TabsContent>
      </Tabs>
      <RunDialog
        run={shownRun}
        open={shownRun !== null}
        onOpenChange={(o) => {
          if (!o) {
            setOpenRun(null);
            if (search.execucao) void navigate({ search: search.aba ? { aba: search.aba } : {} });
          }
        }}
      />
    </>
  );
}

function RulesList({ workspaceId, canWrite, onRun }: { workspaceId: string; canWrite: boolean; onRun: (run: RuleRunDto) => void }) {
  const rules = useQuery(queries7.rules(workspaceId));
  if (rules.isPending) return <LoadingState rows={3} />;
  if (rules.isError) return <ErrorState error={rules.error} onRetry={() => void rules.refetch()} />;
  if (rules.data.length === 0)
    return (
      <EmptyState
        icon={<Shield className="size-8" aria-hidden />}
        title="Nenhuma regra ainda"
        description="Comece por um modelo pronto: pausar anúncio com CPA alto, escalar conjunto com ROAS bom, alertar frequência alta."
        action={
          canWrite ? (
            <Button asChild>
              <Link to="/app/$workspaceId/regras/$ruleId" params={{ workspaceId, ruleId: "nova" }}>
                <Plus /> Nova regra
              </Link>
            </Button>
          ) : undefined
        }
      />
    );
  return (
    <Card>
      <CardContent>
        <ul className="divide-y">
          {rules.data.map((r) => (
            <RuleRow key={r.id} rule={r} workspaceId={workspaceId} canWrite={canWrite} onRun={onRun} />
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function RuleRow({ rule, workspaceId, canWrite, onRun }: { rule: RuleDto; workspaceId: string; canWrite: boolean; onRun: (run: RuleRunDto) => void }) {
  const queryClient = useQueryClient();
  const clients = useQuery(queries.clients(workspaceId));
  const [excluir, setExcluir] = useState(false);
  const [confirmRun, setConfirmRun] = useState(false);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: rulesKey(workspaceId) });
  const simulate = useMutation({
    mutationFn: () => api7.simulateRule(workspaceId, rule.id),
    onSuccess: async (runs) => {
      await invalidate();
      const first = runs[0];
      if (first) onRun(first);
      toast.success(`Simulação: ${runs.reduce((n, r) => n + r.matched, 0)} item(ns) bateriam.`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const run = useMutation({
    mutationFn: () => api7.runRule(workspaceId, rule.id),
    onSuccess: async (runs) => {
      await invalidate();
      await queryClient.invalidateQueries({ queryKey: ["workspaces", workspaceId, "campaigns"] });
      setConfirmRun(false);
      const first = runs[0];
      if (first) onRun(first);
      toast.success(`Executada: ${runs.reduce((n, r) => n + r.executed, 0)} ação(ões).`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const toggle = useMutation({
    mutationFn: () => api7.updateRule(workspaceId, rule.id, { ...rule, active: !rule.active }),
    onSuccess: () => invalidate(),
    onError: (err) => toast.error(errorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: () => api7.deleteRule(workspaceId, rule.id),
    onSuccess: async () => {
      await invalidate();
      toast.success("Regra excluída.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const client = rule.clientId ? clients.data?.find((c) => c.id === rule.clientId) : undefined;
  return (
    <li className="flex flex-col gap-2 py-4 sm:flex-row sm:items-start">
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <Link to="/app/$workspaceId/regras/$ruleId" params={{ workspaceId, ruleId: rule.id }} className="font-medium hover:underline">
            {rule.name}
          </Link>
          <Badge variant={rule.active ? "success" : "secondary"}>{rule.active ? "ativa" : "pausada"}</Badge>
          <Badge variant="outline">{RULE_LEVEL_LABELS[rule.scope.level]}</Badge>
          <Badge variant="outline">a cada {rule.frequencyMinutes} min</Badge>
          {client ? <Badge variant="outline">{client.name}</Badge> : null}
        </div>
        <p className="text-sm text-muted-foreground">{describeRule(rule)}</p>
        <p className="text-xs text-muted-foreground">
          {rule.scope.adAccountMetaIds.length} conta(s) ·{" "}
          {rule.lastRunAt
            ? `última ${rule.lastRunMode === "executar" ? "execução" : "simulação"} ${formatRelative(rule.lastRunAt)} (${rule.lastRunMatched ?? 0} bateu/bateram${rule.lastRunStatus === "erro" ? ", com erro" : ""})`
            : "nunca rodou"}
        </p>
      </div>
      {canWrite ? (
        <div className="flex shrink-0 gap-2">
          <Button variant="outline" size="sm" onClick={() => simulate.mutate()} loading={simulate.isPending}>
            <Play /> Simular
          </Button>
          <Button size="sm" onClick={() => setConfirmRun(true)} loading={run.isPending}>
            <Zap /> Executar agora
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Mais ações">
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to="/app/$workspaceId/regras/$ruleId" params={{ workspaceId, ruleId: rule.id }}>
                  Editar
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => toggle.mutate()}>{rule.active ? "Pausar regra" : "Ativar regra"}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onSelect={() => setExcluir(true)}>
                <Trash2 /> Excluir
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : null}
      <ConfirmDialog
        open={confirmRun}
        onOpenChange={setConfirmRun}
        title="Executar a regra agora?"
        description={`As ações (${describeRule(rule).split(" ENTÃO ")[1] ?? ""}) serão aplicadas na Meta nos itens que baterem nas condições, respeitando os limites de segurança. Prefira "Simular" se quiser só ver o que aconteceria.`}
        confirmLabel="Executar"
        loading={run.isPending}
        onConfirm={() => run.mutate()}
      />
      <ConfirmDialog
        open={excluir}
        onOpenChange={setExcluir}
        title="Excluir a regra?"
        description="O histórico de execuções também será apagado. As alterações já feitas na Meta não são desfeitas."
        confirmLabel="Excluir"
        destructive
        loading={remove.isPending}
        onConfirm={() => remove.mutate()}
      />
    </li>
  );
}

function RunsList({ workspaceId, onOpen }: { workspaceId: string; onOpen: (run: RuleRunDto) => void }) {
  const runs = useQuery(queries7.ruleRuns(workspaceId));
  if (runs.isPending) return <LoadingState rows={3} />;
  if (runs.isError) return <ErrorState error={runs.error} onRetry={() => void runs.refetch()} />;
  if (runs.data.length === 0)
    return <EmptyState icon={<History className="size-8" aria-hidden />} title="Nenhuma execução" description="Simule ou execute uma regra para ver o histórico aqui." />;
  return (
    <Card>
      <CardContent>
        <ul className="divide-y">
          {runs.data.map((r) => (
            <li key={r.id} className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1 space-y-1">
                <p className="text-sm">
                  <span className="font-medium">{r.ruleName}</span>
                  <span className="text-muted-foreground"> · {formatDate(r.startedAt)}</span>
                </p>
                <RunSummary run={r} />
              </div>
              <Button variant="outline" size="sm" onClick={() => onOpen(r)}>
                Ver itens
              </Button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
